import React, { useState, useMemo, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Calendar.css";
import moment from "moment";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const CALENDAR_AUTOCOMPLETE = "one-time-code";

function usePreventAutofill(inputRef) {
    useEffect(() => {
        const input = inputRef.current;
        if (!input || !window.MutationObserver) return undefined;

        const observer = new MutationObserver(() => {
            observer.disconnect();
            input.setAttribute("autocomplete", CALENDAR_AUTOCOMPLETE);
        });
        observer.observe(input, {
            attributes: true,
            attributeFilter: ["autocomplete"],
        });

        return () => observer.disconnect();
    }, [inputRef]);
}

const ExampleCustomInput = React.forwardRef(({ value, onClick, label, disabled, fieldName }, ref) => {
    const inputRef = React.useRef(null);
    React.useImperativeHandle(ref, () => inputRef.current);
    usePreventAutofill(inputRef);

    return (
        <TextField
            variant="outlined"
            label={label}
            onClick={onClick}
            inputRef={inputRef}
            value={value}
            size="small"
            fullWidth
            placeholder={label}
            disabled={disabled}
            autoComplete={CALENDAR_AUTOCOMPLETE}
            name={fieldName ? `seguro-fecha-${fieldName}` : "seguro-fecha"}
            InputProps={{
                style: {
                    borderRadius: "20px",
                },
            }}
        />
    );
});
ExampleCustomInput.displayName = "ExampleCustomInput";

export default function CalendarPicker(props) {
    const { i18n } = useTranslation();
    const isSpanish = i18n.language?.startsWith('es');
    const [msg, setMsg] = useState("");
    const [view, setView] = useState(props.startWithYear ? "year" : "date");

    const onChange = (date) => {
        if (view !== "date") return;

        if (date) {
            props.onChange(date.toISOString(), props.name);
            setMsg(null);
        } else {
            props.onChange(null, props.name);
        }
    };


    const selectedDate = useMemo(() => (props.value ? new Date(props.value) : null), [props.value]);

    // Determine if the selected date is the same as the minDate
    const isSameDay = useMemo(() => {
        if (!selectedDate || !props.minDate) {
            return false;
        }
        return moment(selectedDate).isSame(props.minDate, 'day');
    }, [selectedDate, props.minDate]);

    return (
        <>
            <DatePicker
                selected={selectedDate}
                onChange={onChange}
                onCalendarOpen={() => setView(props.startWithYear ? "year" : "date")}
                onSelect={() => {
                    if (view === "year") setView("month");
                    else if (view === "month") setView("date");
                }}
                showYearPicker={view === "year"}
                showMonthYearPicker={view === "month"}
                shouldCloseOnSelect={view === "date"}
                showTimeSelect={props.subtype === "datetime"}
                dateFormat={props.subtype === "datetime"
                    ? (isSpanish ? "dd/MM/yyyy HH:mm" : "MM/dd/yyyy HH:mm")
                    : (isSpanish ? "dd/MM/yyyy" : "MM/dd/yyyy")
                }
                minDate={props.minDate}
                maxDate={props.maxDate}
                minTime={isSameDay ? props.minDate : undefined}
                maxTime={isSameDay ? moment(props.minDate).endOf("day").toDate() : undefined}
                excludeTimes={props.excludeTimes}
                filterTime={props.filterTime}
                wrapperClassName={props.className || "w-full"}
                popperClassName="calendar-popper"
                portalId="root"
                disabled={props.readOnly}
                customInput={<ExampleCustomInput label={props.label} fieldName={props.name} />}
            />
            {msg && <div style={{ color: "red" }}>{msg}</div>}
        </>
    );
}

CalendarPicker.propTypes = {
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    onChange: PropTypes.func,
    name: PropTypes.string,
    label: PropTypes.string,
    subtype: PropTypes.oneOf(['date', 'datetime']),
    startWithYear: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    excludeTimes: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
    filterTime: PropTypes.func,
    className: PropTypes.string,
    readOnly: PropTypes.bool
};

CalendarPicker.defaultProps = {
    subtype: 'date'
};
